import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  isGeneratingPreviewSelector,
  isSuggestionUnresolvedSelector,
} from 'blocks/ClipSelect/selectors';
import { ClipSuggestion } from 'blocks/ClipSelect/types';
import useSet from 'hooks/useSet';
import { pushModal } from 'redux/modules/modal';
import { Dispatch } from 'redux/types';
import { useClipSelectNavigation } from '../ClipSelectNavigationContext';
import {
  BoundClipSelectContextType,
  ClipSelectContextType,
  ClipSelectionType,
  ClipSelectProviderProps,
  OnSelectClip,
  UseClipSelectProps,
} from './types';
import useDefaultHighlightedRegion from './useDefaultHighlightedRegion';
import useEddyTranscript from './useEddyTranscript';
import usePlayback from './usePlayback';
import useReprocessClip from './useReprocessClip';
import useSuggestions from './useSuggestions';

const ClipSelectContext = React.createContext<
  ClipSelectContextType | undefined
>(undefined);

export const ClipSelectProvider: React.FC<ClipSelectProviderProps> = props => {
  const {
    requestId,
    eddyProjectId,
    suggestedClips: suggestedClipsProp,
    dislikedSuggestionIds,
    clipsPageInfo,
    mediaType,
    onReprocessClip: onReprocessClipProp,
    onSubmit,
    onDislikeClip,
    onOpenInAdvancedEditor,
    onSelectClip,
    onUpdateSuggestions,
    onUpdateSuggestion,
    onDownloadAll,
    onExportAll,
    children,
  } = props;

  const [clipSelectionType, setClipSelectionType] = useState<
    ClipSelectionType | undefined
  >();

  const dispatch = useDispatch<Dispatch>();

  const [
    exportingSuggestionIds,
    { add: addExportingSuggestionId, remove: removeExportingSuggestionId },
  ] = useSet<number>();

  const {
    findSuggestion,
    hasClipSuggestions,
    removeSuggestion,
    updateSuggestionProjectName,
    suggestedClips,
    suggestionIds,
    isGeneratingPreviews,
    visibleSuggestionIds,
    updatedSuggestionProjectNames,
  } = useSuggestions({
    suggestedClips: suggestedClipsProp,
  });

  const {
    cancelTranscriptUpdate,
    eddySuggestedClips,
    onTranscriptUpdate,
  } = useEddyTranscript({
    onUpdateSuggestions,
    suggestedClips,
  });

  const [activeSuggestionId, setActiveSuggestionId] = useState<number | null>(
    null,
  );

  const activeSuggestion = useMemo(() => findSuggestion(activeSuggestionId), [
    activeSuggestionId,
    findSuggestion,
  ]);

  const {
    defaultClipperRegion,
    defaultTranscriptRegion,
    highlightRegion,
  } = useDefaultHighlightedRegion({
    mediaDurationMillis: clipsPageInfo.mediaDurationMillis,
    suggestedClips,
    visibleSuggestionIds,
  });

  const { onReprocessClip } = useReprocessClip({
    onReprocessClip: onReprocessClipProp,
  });

  const {
    audioClipperRef,
    pauseAll,
    isSuggestionPlaying,
    videoClipperPlayerControls,
  } = usePlayback();

  const [, send] = useClipSelectNavigation();

  const handleAdjust = useCallback(() => {
    if (activeSuggestion) {
      setClipSelectionType(ClipSelectionType.SUGGESTED_CLIP);
      highlightRegion(activeSuggestion.id);
      send({ type: 'ADJUST_CLICK' });
    }
  }, [activeSuggestion, highlightRegion, send]);

  const handleEditTemplate = useCallback(
    async (suggestionId: number) => {
      const suggestion = findSuggestion(suggestionId);

      if (suggestion) {
        const result = await dispatch(
          pushModal({
            name: 'EditTemplate',
            params: {
              projectId: suggestion.projectUuid,
              widgetId: suggestion?.widgetId,
              features: {
                progress: true,
              },
            },
          }),
        );

        const newEmbedConfig = result?.newEmbedConfig;

        if (newEmbedConfig) {
          onReprocessClip(suggestion.id, {
            configJson: newEmbedConfig,
          });
        }
      }
    },
    [dispatch, findSuggestion, onReprocessClip],
  );

  const handleCustom = useCallback(() => {
    setClipSelectionType(ClipSelectionType.CUSTOM_CLIP);
    highlightRegion();
    send({ type: 'CUSTOM_CLICK' });
  }, [highlightRegion, send]);

  const handleShare = useCallback(
    (suggestionId: number): Promise<string | undefined> => {
      const suggestion = findSuggestion(suggestionId);
      return onSubmit?.(suggestion) ?? Promise.resolve(undefined);
    },
    [findSuggestion, onSubmit],
  );

  const exportClip = useCallback(
    async (
      suggestionId,
      cb?: (suggestion: ClipSuggestion) => Promise<void>,
    ) => {
      if (exportingSuggestionIds.has(suggestionId)) {
        return;
      }

      addExportingSuggestionId(suggestionId);

      try {
        cancelTranscriptUpdate();
        const suggestion = await onUpdateSuggestion(suggestionId);

        if (suggestion) {
          if (suggestion.isVideoOutdated) {
            await onReprocessClip(suggestionId);
          }

          await cb?.(suggestion);
        }
      } finally {
        removeExportingSuggestionId(suggestionId);
      }
    },
    [
      addExportingSuggestionId,
      cancelTranscriptUpdate,
      exportingSuggestionIds,
      onReprocessClip,
      onUpdateSuggestion,
      removeExportingSuggestionId,
    ],
  );

  const handleSelectClip: OnSelectClip = async ({
    region: regionOverride,
  }): Promise<number | void> => {
    pauseAll();

    if (!suggestionIds.length) {
      send({ type: 'SUBMIT_SUCCESS' });
    }

    const suggestionId = await onSelectClip({
      region: regionOverride,
      activeSuggestionId:
        clipSelectionType === ClipSelectionType.SUGGESTED_CLIP
          ? activeSuggestionId
          : undefined,
    });

    setClipSelectionType(undefined);

    return suggestionId;
  };

  const handleRemoveSuggestion = useCallback(
    (id: number) => {
      pauseAll();
      removeSuggestion(id);
    },
    [pauseAll, removeSuggestion],
  );

  const handleOpenInAdvancedEditor = useCallback(
    (suggestionId: number): Promise<string | undefined> =>
      onOpenInAdvancedEditor?.(findSuggestion(suggestionId)) ??
      Promise.resolve(undefined),
    [findSuggestion, onOpenInAdvancedEditor],
  );

  useEffect(() => {
    send({
      type: 'DATA_LOAD_SUCCESS',
      payload: {
        hasTranscript: !!eddyProjectId,
        suggestionCount: suggestionIds.length,
      },
    });
  }, [eddyProjectId, highlightRegion, send, suggestionIds]);

  const handleDislikeClip: ClipSelectProviderProps['onDislikeClip'] = React.useCallback(
    (dislikeReason, suggestion): void => {
      onDislikeClip(dislikeReason, suggestion, () =>
        removeSuggestion(suggestion.id),
      );
    },
    [onDislikeClip, removeSuggestion],
  );

  return (
    <ClipSelectContext.Provider
      value={{
        requestId,
        activeSuggestion,
        activeSuggestionId: activeSuggestion?.id,
        audioClipperRef,
        clipsPageInfo,
        defaultClipperRegion,
        defaultTranscriptRegion,
        dislikedSuggestionIds,
        eddyProjectId,
        exportClip,
        exportingSuggestionIds,
        hasClipSuggestions,
        clipSelectionType,
        isClipSuggestionLoading: activeSuggestion?.status !== 'completed',
        isSuggestionPlaying,
        isGeneratingPreviews,
        mediaType,
        eddySuggestedClips,
        onTranscriptUpdate,
        onAdjust: handleAdjust,
        onEditTemplate: handleEditTemplate,
        onCustom: handleCustom,
        onDislikeClip: handleDislikeClip,
        prepareAdvancedEditorUrl: handleOpenInAdvancedEditor,
        onReprocessClip,
        onSelectClip: handleSelectClip,
        prepareShareUrl: handleShare,
        onSuggestionSelect: setActiveSuggestionId,
        onUpdateSuggestions,
        onUpdateSuggestion,
        removeSuggestion: handleRemoveSuggestion,
        updateSuggestionProjectName,
        suggestedClips,
        suggestionIds,
        videoPlayerControls: videoClipperPlayerControls,
        visibleSuggestionIds,
        updatedSuggestionProjectNames,
        pauseAll,
        onDownloadAll,
        onExportAll,
      }}
    >
      {children}
    </ClipSelectContext.Provider>
  );
};

export function useClipSelect(): ClipSelectContextType;
export function useClipSelect(
  props: UseClipSelectProps,
): BoundClipSelectContextType;
export function useClipSelect(
  props?: UseClipSelectProps,
): ClipSelectContextType | BoundClipSelectContextType {
  const context = useContext(ClipSelectContext);

  const clipId = props?.clipId;

  const suggestion = useMemo(() => {
    if (clipId === undefined) {
      return undefined;
    }

    return context.suggestedClips.find(s => s.id === clipId);
  }, [clipId, context.suggestedClips]);

  const updatedSuggestionProjectName = useMemo(() => {
    if (clipId === undefined) {
      return undefined;
    }

    return context.updatedSuggestionProjectNames[clipId];
  }, [clipId, context.updatedSuggestionProjectNames]);

  const boundExportClip = useCallback(
    (cb: (suggestion: ClipSuggestion) => Promise<void>) =>
      context.exportClip(clipId, cb),
    [clipId, context],
  );

  const boundPrepareShareUrl = useCallback(
    () => context.prepareShareUrl(clipId),
    [clipId, context],
  );

  const boundOnEditTemplate = useCallback(
    () => context.onEditTemplate(clipId),
    [clipId, context],
  );

  const boundPrepareAdvancedEditorUrl = useCallback(
    () => context.prepareAdvancedEditorUrl(clipId),
    [clipId, context],
  );

  const boundRemoveSuggestion = useCallback(() => {
    context.removeSuggestion(clipId);
  }, [clipId, context]);

  const boundUpdateSuggestionProjectName = useCallback(
    (projectName: string) => {
      context.updateSuggestionProjectName(clipId, projectName);
    },
    [clipId, context],
  );

  const boundOnReprocessClip = useCallback(
    opts => context.onReprocessClip(clipId, opts),
    [clipId, context],
  );

  const boundOnSuggestionSelect = useCallback(
    (id?: null) => {
      if (id === null) {
        context.onSuggestionSelect(null);
      } else {
        context.onSuggestionSelect(clipId);
      }
    },
    [clipId, context],
  );

  const boundOnUpdateSuggestion = useCallback(
    () => context.onUpdateSuggestion(clipId),
    [clipId, context],
  );

  const isSuggestionUnresolved = isSuggestionUnresolvedSelector(suggestion);

  const isExporting = useMemo(
    () =>
      context.exportingSuggestionIds.has(clipId) ||
      (isSuggestionUnresolved && !suggestion?.isPreviewRequested),
    [
      clipId,
      context.exportingSuggestionIds,
      isSuggestionUnresolved,
      suggestion?.isPreviewRequested,
    ],
  );

  const isGeneratingPreview = isGeneratingPreviewSelector(suggestion);

  const boundContext: BoundClipSelectContextType = useMemo(
    () => ({
      ...context,
      isExporting,
      isGeneratingPreview,
      exportClip: boundExportClip,
      onEditTemplate: boundOnEditTemplate,
      prepareAdvancedEditorUrl: boundPrepareAdvancedEditorUrl,
      onReprocessClip: boundOnReprocessClip,
      prepareShareUrl: boundPrepareShareUrl,
      onSuggestionSelect: boundOnSuggestionSelect,
      onUpdateSuggestion: boundOnUpdateSuggestion,
      removeSuggestion: boundRemoveSuggestion,
      updateSuggestionProjectName: boundUpdateSuggestionProjectName,
      suggestion,
      updatedSuggestionProjectName,
    }),
    [
      boundExportClip,
      boundOnEditTemplate,
      boundPrepareAdvancedEditorUrl,
      boundOnReprocessClip,
      boundPrepareShareUrl,
      boundOnSuggestionSelect,
      boundOnUpdateSuggestion,
      boundRemoveSuggestion,
      boundUpdateSuggestionProjectName,
      context,
      isExporting,
      isGeneratingPreview,
      suggestion,
      updatedSuggestionProjectName,
    ],
  );

  if (context === undefined) {
    throw new Error('useClipSelect must be used within ClipSelectProvider');
  }

  return clipId !== undefined ? boundContext : context;
}
